import { Link, useLocation } from 'react-router-dom';
import { Home, Dumbbell, LineChart, Apple, Wand2, User, Users } from 'lucide-react';
import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useAuthStore } from '@/store/authStore';

export function PerformanceDock() {
    const location = useLocation();

    const { isParent, isCoach } = useAuthStore();

    // Define items dynamically based on role
    const navItems = useMemo(() => {
        if (isCoach) {
            return [
                { id: 'team', path: '/team', icon: Users, label: 'Team' },
                { id: 'profile', path: '/profile', icon: User, label: 'Me' }
            ];
        }
        if (isParent) {
            return [
                { id: 'athletes', path: '/athletes', icon: Users, label: 'Athletes' },
                { id: 'profile', path: '/profile', icon: User, label: 'Parent' }
            ];
        }
        return [
            { id: 'home', path: '/', icon: Home, label: 'Home' },
            { id: 'train', path: '/train', icon: Dumbbell, label: 'Train' },
            { id: 'progress', path: '/progress', icon: LineChart, label: 'Progress' },
            { id: 'analysis', path: '/analysis', icon: Wand2, label: 'Analyze' },
            { id: 'nutrition', path: '/nutrition', icon: Apple, label: 'Fuel' },
            { id: 'profile', path: '/profile', icon: User, label: 'Me' },
        ];
    }, [isParent, isCoach]);

    return (
        <div className="fixed bottom-6 left-0 right-0 z-50 flex justify-center px-4 pointer-events-none">
            <motion.nav
                initial={{ y: 100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{
                    type: "spring",
                    stiffness: 260,
                    damping: 20,
                    delay: 0.2
                }}
                className="pointer-events-auto relative flex items-center bg-bg-surface/80 backdrop-blur-xl border border-white/[0.08] rounded-full px-2 h-[72px] shadow-[0_10px_40px_rgba(0,0,0,0.5)]"
            >
                {navItems.map((item) => {
                    const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));

                    return (
                        <Link
                            key={item.path}
                            to={item.path}
                            className="relative flex flex-col items-center justify-center w-16 h-16 rounded-full group cursor-pointer"
                        >
                            {/* Fluid Background Pill */}
                            {isActive && (
                                <motion.div
                                    layoutId="dock-active-pill"
                                    className="absolute inset-2 bg-gradient-to-t from-gold-500/20 to-transparent rounded-2xl z-0"
                                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                >
                                    {/* Inner Glow to simulate glass reflection */}
                                    <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gold-500/50 to-transparent" />
                                </motion.div>
                            )}

                            {/* Active Dot Indicator */}
                            {isActive && (
                                <motion.div
                                    layoutId="dock-active-dot"
                                    className="absolute bottom-2 w-1 h-1 rounded-full bg-gold-500 z-20"
                                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                />
                            )}

                            {/* Icon with Micro-interaction */}
                            <motion.div
                                animate={{
                                    y: isActive ? -4 : 0,
                                    scale: isActive ? 1.1 : 1,
                                    color: isActive ? '#FFD000' : 'rgba(255,255,255,0.45)'
                                }}
                                whileHover={{ scale: 1.15, rotate: [0, -5, 5, 0] }}
                                transition={{ type: "spring", stiffness: 400, damping: 17 }}
                                className="relative z-10"
                            >
                                <item.icon
                                    size={24}
                                    strokeWidth={isActive ? 2.5 : 2}
                                />
                            </motion.div>

                            {/* Hover Highlight (Non-active items) */}
                            {!isActive && (
                                <div className="absolute inset-2 rounded-2xl bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-0" />
                            )}
                        </Link>
                    );
                })}
            </motion.nav>
        </div>
    );
}
